import { MARKETS } from "../lib/content";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export default function GlobalTradeNetwork() {
  const [hover, setHover] = useState(null);

  return (
    <section
      className="relative py-24 sm:py-32 bg-[#0A1F3F] text-white overflow-hidden"
      data-testid="global-trade-network"
    >
      <img
        src="https://static.prod-images.emergentagent.com/jobs/992269a5-240d-47a2-94bf-57af67715604/images/107173358a58b4fd951837fe4813b7bcfbd17e66dd1538255a609f5864d5d57d.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-30"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F3F]/85 via-[#0A1F3F]/70 to-[#0A1F3F]/95" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-px bg-[#F37021]" />
              <span className="text-eyebrow text-[#F37021]">Global Reach</span>
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              GLOBAL TRADE
              <br />
              NETWORK
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl">
              BNM Group connects suppliers and buyers across strategic
              international markets through a global trade network.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-4">
              {MARKETS.map((m, i) => (
                <motion.button
                  key={m.id}
                  onMouseEnter={() => setHover(m.id)}
                  onMouseLeave={() => setHover(null)}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`market-pill-${m.id}`}
                  className={`text-left py-4 px-5 border transition-colors ${
                    hover === m.id
                      ? "border-[#F37021] bg-[#F37021]/10"
                      : "border-[#1E3966] bg-[#0F2852]/30"
                  }`}
                >
                  <div className="font-display font-bold text-xl">{m.name}</div>
                  <div className="text-[11px] text-white/60 uppercase tracking-[0.18em] mt-1">
                    {m.countries.length} markets
                  </div>
                </motion.button>
              ))}
            </div>

            <Link
              to="/markets"
              data-testid="markets-view-all"
              className="mt-10 inline-flex items-center gap-3 bg-[#F37021] hover:bg-[#D35F16] text-white px-8 py-5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors"
            >
              View All Markets
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="relative aspect-[16/11] bg-gradient-to-br from-[#0F2852] to-[#071833] border border-[#1E3966] overflow-hidden">
              <WorldMapSVG />
              {MARKETS.map((m) => (
                <motion.div
                  key={m.id}
                  style={{ top: m.position.top, left: m.position.left }}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  onMouseEnter={() => setHover(m.id)}
                  onMouseLeave={() => setHover(null)}
                  data-testid={`map-pin-${m.id}`}
                >
                  <div className="relative">
                    <span className="absolute inset-0 w-6 h-6 rounded-full bg-[#F37021] animate-ping opacity-60" />
                    <span className="relative block w-6 h-6 rounded-full bg-[#F37021] border-2 border-white" />
                  </div>
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 top-8 whitespace-nowrap px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold bg-white text-[#0A1F3F] transition-opacity ${
                      hover === m.id ? "opacity-100" : "opacity-80"
                    }`}
                  >
                    {m.name}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WorldMapSVG() {
  // Dotted abstract world map
  return (
    <svg
      viewBox="0 0 1000 600"
      className="absolute inset-0 w-full h-full opacity-40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <pattern id="dots" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.1" fill="#A9B4C8" fillOpacity="0.6" />
        </pattern>
      </defs>
      {/* Rough continent silhouettes */}
      <path
        d="M80,220 Q150,180 220,200 T340,220 L360,280 Q320,320 260,310 T140,320 Q90,300 80,220Z"
        fill="url(#dots)"
      />
      <path
        d="M400,120 Q470,90 540,110 Q620,100 680,130 L700,180 Q660,230 600,230 T460,240 Q410,220 400,180 Z"
        fill="url(#dots)"
      />
      <path
        d="M430,260 Q500,250 560,270 Q640,260 700,290 L720,360 Q680,400 620,410 T480,420 Q440,410 420,370 Z"
        fill="url(#dots)"
      />
      <path
        d="M720,140 Q790,130 860,160 Q920,180 940,230 L930,300 Q880,320 820,310 T740,280 Q720,230 720,140 Z"
        fill="url(#dots)"
      />
      <path
        d="M260,360 Q340,380 400,400 Q460,420 500,460 L500,540 Q440,560 380,540 T280,500 Q250,460 260,360 Z"
        fill="url(#dots)"
      />
      <path
        d="M780,340 Q860,360 920,400 L900,480 Q820,500 760,470 T740,400 Z"
        fill="url(#dots)"
      />
    </svg>
  );
}
